import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Loader2, Eye, EyeOff, CheckCircle2, KeyRound } from 'lucide-react';
import { BrandLogo } from '../components/BrandLogo';

/**
 * First-login password rotation. Reached automatically when /me.must_change_password
 * is true (set by /api/admin/users/create + admin force-resets in some flows).
 */
export default function ChangePassword() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  // Magic-link onboarded users had a random server-seeded password; we skip
  // the "current password" prompt for them.
  const seeded = !!user?.password_seeded;

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (next !== confirm) { setErr('New passwords do not match.'); return; }
    if (next.length < 8) { setErr('Use at least 8 characters with upper, lower and a digit.'); return; }
    setLoading(true);
    try {
      const body = seeded
        ? { new_password: next }
        : { current_password: current, new_password: next };
      await api.post('/auth/change-password', body);
      if (refreshUser) await refreshUser();
      const dest = user?.role === 'patient' ? '/patient' : '/admin';
      navigate(dest, { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || formatApiError(e));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-surface">
      <div className="w-full max-w-md surface border border-soft rounded-xl shadow p-8">
        <BrandLogo />
        <div className="mt-6">
          <div className="text-xs uppercase tracking-[0.2em] text-brand font-semibold mb-2">FIRST LOGIN · MANDATORY</div>
          <h1 className="text-2xl font-heading font-bold tracking-tight flex items-center gap-2">
            <KeyRound className="w-6 h-6 text-brand"/> Change your password
          </h1>
          <p className="text-sm text-fg-muted mt-2">
            {seeded
              ? 'Welcome aboard! Set a password for your account so you can sign in next time.'
              : 'For security, you must replace the temporary password before continuing.'}
          </p>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          {!seeded && (
            <Field label="Current (temporary) password">
              <div className="relative">
                <input type={show ? 'text' : 'password'} required className="input pr-10"
                       value={current} onChange={e=>setCurrent(e.target.value)} data-testid="cur-pw" autoFocus/>
                <button type="button" onClick={()=>setShow(s=>!s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-muted hover:text-fg" data-testid="toggle-pw">
                  {show ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                </button>
              </div>
            </Field>
          )}
          <Field label="New password (≥8 chars, upper + lower + digit)">
            <input type={show ? 'text' : 'password'} required className="input"
                   value={next} onChange={e=>setNext(e.target.value)} data-testid="new-pw"
                   autoFocus={seeded}/>
          </Field>
          <Field label="Confirm new password">
            <input type={show ? 'text' : 'password'} required className="input"
                   value={confirm} onChange={e=>setConfirm(e.target.value)} data-testid="new-pw2"/>
          </Field>
          {err && <div className="text-sm text-[#B83A3A]" data-testid="cp-err">{err}</div>}
          <button type="submit" disabled={loading || (!seeded && !current) || !next || !confirm}
                  className="btn-primary w-full inline-flex items-center justify-center gap-2"
                  data-testid="cp-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle2 className="w-4 h-4"/>}
            {seeded ? 'Set my password' : 'Update password'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-fg-muted mb-1">{label}</div>
      {children}
    </label>
  );
}
